// AppWrapper.js  (Updated - Final Version)
import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import axios from 'axios';

import Login from './Login';
import Home from './Home';
import ReturnHandler from './ReturnHandler';
import Admin from './Admin';
import OrderSimulate from './OrderSimulate';
import Contact from './Contact';
import Terms from './Terms';
import StoreLocator from './StoreLocator';

import tbsLogo from './images/logos/beer-logo.svg';
import storeIcon from './images/assets/store.svg';
import userIcon from './images/assets/user-icon.svg';
import hamburgerIcon from './images/assets/Menu.svg';
import glassIcon from './images/assets/glass.svg';

function AppWrapper() {
    const location = useLocation();
    const hideHeaderOn = ['/kiosk'];
    const hideFooterOn = ['/', '/login'];
    const showHeader = !hideHeaderOn.includes(location.pathname);
    const showFooter = !hideFooterOn.includes(location.pathname);

    const {
        loggedIn,
        setLoggedIn,
        userName,
        setUserName,
        adminUser,
        setAdminUser,
        adminAuthLevel,
        setAdminAuthLevel,
        viewedUser,
        setViewedUser
    } = useContext(AuthContext);

    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const navigate = useNavigate();

    // Improved displayName - returns empty string when no name
    const displayName = useMemo(() => {
        if (viewedUser?.user_name) {
            return viewedUser.user_name;
        }
        if (userName && userName.trim() !== '') {
            return userName;
        }
        return '';               
    }, [viewedUser, userName]);

    const handleLogout = async () => {
        try {
            await axios.post('/api/logout', {}, { withCredentials: true });

            setAdminUser(false);
            setAdminAuthLevel(null);
            setLoggedIn(false);
            setViewedUser(null);
            setUserName('');
            setSearchTerm('');
            setSearchResults([]);
            setShowResults(false);

            navigate('/', { replace: true });
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const handleSearchChange = (event) => {
        const term = event.target.value.trim();
        setSearchTerm(term);

        if (term.length >= 3) {
            fetchSearchResults(term);
            setShowResults(true);
        } else {
            setShowResults(false);
            setSearchResults([]);
        }
    };

    const fetchSearchResults = async (term) => {
        try {
            const response = await fetch(`/api/userSearch?query=${encodeURIComponent(term)}`);
            if (!response.ok) throw new Error('Search failed');
            const data = await response.json();
            setSearchResults(data || []);
        } catch (error) {
            console.error('Error fetching search results:', error);
            setSearchResults([]);
        }
    };

    const handleItemClick = (customer) => {
        setViewedUser(customer);
        setSearchTerm('');
        setSearchResults([]);
        setShowResults(false);
        closeMenu();
    };

    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
    const closeMenu = () => setIsMenuOpen(false);

    const navToAdmin = () => {
        if (adminUser) {
            navigate('/admin');
            closeMenu();
        }
    };

    const navHome = () => {
        navigate('/home');
        closeMenu();
    };

    return (
        <div>
            {showHeader && (
            <nav className="navBar">
                {!isMenuOpen && (
                    <button className="hamburger" onClick={toggleMenu} aria-label="Open menu">
                        <img src={hamburgerIcon} alt="Menu" />
                    </button>
                )}

                <div className="lsNav" onClick={showFooter ? navHome : undefined}>
                    <img src={tbsLogo} className="tbsLogo" alt="TBS Logo" />
                </div>

                <div className="desktop-nav">
                    {adminUser && (
                        <div className="search-container">
                            <input
                                type="text"
                                placeholder="Search for a Customer"
                                value={searchTerm}
                                onChange={handleSearchChange}
                            />
                            <img src={glassIcon} alt="Search" className="search-icon" />

                            {showResults && searchResults.length > 0 && (
                                <ul className="search-results">
                                    {searchResults.map((customer, index) => (
                                        <li key={index} onClick={() => handleItemClick(customer)}>
                                            {customer.user_name}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {loggedIn && (
                        <div className="rsNav">
                            <div className="userBar">
                                <div className="storeIconDiv">
                                    <img src={storeIcon} className="storeIcon" alt="Store" />
                                </div>
                                {displayName && <span>{displayName}</span>}   {/* ← Only render span if name exists */}
                            </div>

                            {adminAuthLevel > 1 && (
                                <div className="storeIconDiv" onClick={navToAdmin}>
                                    <img src={userIcon} alt="Admin" />
                                </div>
                            )}

                            <button onClick={handleLogout}>Log Out</button>
                        </div>
                    )}
                </div>

                {/* Mobile Search Bar */}
                {adminUser && (
                    <div className="mobile-search-bar">
                        <input
                            type="text"
                            placeholder="Search for a Customer"
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                        <img src={glassIcon} alt="Search" className="search-icon" />

                        {showResults && searchResults.length > 0 && (
                            <ul className="search-results mobile-search-results">
                                {searchResults.map((customer, index) => (
                                    <li key={index} onClick={() => handleItemClick(customer)}>
                                        {customer.user_name}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {/* Mobile Menu */}
                {isMenuOpen && (
                    <div className="mobile-menu" onClick={closeMenu}>
                        <button className="close-btn" onClick={closeMenu}>×</button>
                        <div className="mobile-menu-content" onClick={e => e.stopPropagation()}>
                            {loggedIn && (
                                <>
                                    {displayName && (
                                        <div className="mobile-user">
                                            <img src={storeIcon} alt="Store" />
                                            <span>{displayName}</span>
                                        </div>
                                    )}
                                    {adminUser && (
                                        <div className="mobile-item" onClick={navToAdmin}>
                                            <img src={userIcon} alt="Admin" />
                                            <span>Admin Panel</span>
                                        </div>
                                    )}
                                    <div
                                        className="mobile-item"
                                        onClick={() => { handleLogout(); closeMenu(); }}
                                    >
                                        <img src={userIcon} alt="Logout" />
                                        <span>Log Out</span>
                                    </div>
                                </>
                            )}
                            {!loggedIn && <div className="mobile-item">Please log in</div>}
                        </div>
                    </div>
                )}
            </nav>
            )}

            <Routes>
                <Route path="/" element={<Login />} />
                <Route path="/home" element={<Home />} />
                <Route path="/return" element={<ReturnHandler />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/moq" element={<OrderSimulate />} />
                <Route path="/login" element={<Login />} />
                <Route path="/contact-us" element={<Contact />} />
                <Route path="/terms-and-conditions" element={<Terms />} />
                <Route path="/store-locator" element={<StoreLocator />} />
            </Routes>

            {showFooter && (
                <footer>
                    <div className='footer-content'>
                        <div className='footer-logo-container'>
                            <img src={tbsLogo} alt="TBS Logo" />
                        </div>
                        <div className='footer-information'>
                            <div className='footer-into-content'>
                                <div className='footer-content-header'>Information</div>
                                <div className='footer-content-detail'>
                                    <a href="/contact-us">Contact Us</a>
                                </div>
                            </div>
                            <div className='footer-into-content'>
                                <div className='footer-content-header'>Legal & Policies</div>
                                <div className='footer-content-detail'>
                                    <a href="/terms-and-conditions">Terms and Conditions</a>
                                </div>
                                <div className='footer-content-detail'>
                                    <a href="https://www.thebeerstore.ca/privacy-policy" target="_blank" rel="noopener noreferrer">
                                        Privacy Policy
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </footer>
            )}
        </div>
    );
}

export default AppWrapper;